import React, { useEffect, useRef } from 'react';

import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { $retailMediaAtom } from '@client/core/atoms/retailMedia.js';
import { useInScreen } from '@client/core/hooks/index.js';
import { useStore } from '@nanostores/react';
import type { LayoutType } from '@schibsted-nmp/advertising-shared';
import {
  DisplayPlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';
import {
  BapSearchAd,
  AdImage
} from '@schibsted-nmp/search-frontend-components/react';

import type { RetailMediaProduct } from '../types/index.js';
import {
  trackClickBackend,
  trackImpressionBackend
} from '../utils/RetailMediaApi.js';
import {
  trackImpressionPulse,
  trackClickPulse
} from '../utils/RetailMediaPulse.js';

export type SimpleBapSearchAdProps = {
  layout?: LayoutType;
  onClick?: () => void;
  products: Array<RetailMediaProduct>;
  placementId?: string;
  categories?: string[] | undefined;
};

export type LabelRenderProps = {
  className?: string;
  classes: {
    badge: string;
    info: string;
    warning: string;
    success: string;
    [key: string]: string;
  };
};

function optimizeImageUrl(url: string): string {
  const params = new URLSearchParams({
    w: '320',
    q: '30',
    f: 'webp'
  });
  return `${url}?${params.toString()}`;
}

function convertToBapFormat(product: RetailMediaProduct) {
  return {
    ad_id: product.adId,
    canonical_url: product.productUrl,
    distance: null,
    brand: '',
    extras: [],
    flags: ['SPONSORED'],
    heading: product.title,
    image: {
      aspect_ratio: 1.3333333333333333,
      height: 400,
      url: optimizeImageUrl(product.imageUrl),
      width: 533
    },
    labels: [],
    location: '',
    main_search_key: 'SEARCH_ID_BAP_COMMON',
    organisation_name: product.advertiser,
    price: {
      currency_code: product.currency,
      amount: product.price,
      price_unit: 'kr'
    },
    timestamp: undefined,
    trade_type: 1
  };
}

export function SponsoredProductAd({
  layout: initialLayout,
  products,
  placementId,
  categories
}: SimpleBapSearchAdProps) {
  const product = products[0];

  // Subscribe to atom for layout changes to re-render component as grid/list
  const retailMediaState = useStore($retailMediaAtom);
  const layout = retailMediaState.layoutType ?? initialLayout ?? 'grid';

  const adRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);

  // Track viewability
  const { hasIntersected } = useInScreen({
    ref: adRef,
    threshold: 0.5,
    rootMargin: '0px'
  });

  useEffect(() => {
    if (hasIntersected && !hasTrackedView.current && product) {
      hasTrackedView.current = true;
      updateMetricByKey(
        DisplayPlacementId.AdvtSplMiddle1,
        PlacementStatus.AdViewed
      );

      // Track impression server-side to avoid ad blockers
      void trackImpressionBackend(product.impressionUrl);
      trackImpressionPulse(product, {
        zone: 'recommerce_search',
        categories,
        ...(placementId && { placementId })
      });
    }
  }, [hasIntersected, product]);

  const handleClick = () => {
    if (product) {
      // Track click server-side to avoid ad blockers
      void trackClickBackend(product.clickUrl);
      trackClickPulse(product, {
        zone: 'recommerce_search',
        categories,
        ...(placementId && { placementId })
      });
    }
  };

  if (!product) return null;

  const bapAd = convertToBapFormat(product);

  const IMAGE_SIZES =
    '(min-width: 1300px) 306px, (min-width: 1010px) 316px, (min-width: 768px) 33vw, (min-width: 480px) 50vw, 100vw';

  return (
    <>
      <div ref={adRef}>
        <BapSearchAd
          ad={bapAd}
          id={bapAd.ad_id}
          imageSizes={IMAGE_SIZES}
          layout={layout}
          url={bapAd.canonical_url}
          target="_blank"
          onClick={handleClick}
          className={`sponsored-product-ad sponsored-product-ad--${layout}`}
          label={() => (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: 'none',
                zIndex: 2
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  fontSize: 'var(--w-font-size-xs)',
                  lineHeight: 'var(--w-line-height-xs)',
                  borderRadius: '0 0 4px 0',
                  padding: '.4rem .8rem',
                  backgroundColor: '#FFF',
                  color: 'var(--w-s-color-text)'
                }}
              >
                Reklame
              </span>
              <a
                href={product.dsaUrl}
                target="_blank"
                title="Ad info"
                aria-label="Ad info"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  display: 'flex',
                  width: '20px',
                  height: '24px',
                  padding: '4px 2px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '0 0 0 4px',
                  background: '#FFF',
                  border: 'none',
                  cursor: 'pointer',
                  pointerEvents: 'auto'
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M8 4C7.30964 4 6.75 3.44036 6.75 2.75C6.75 2.05964 7.30964 1.5 8 1.5C8.69036 1.5 9.25 2.05964 9.25 2.75C9.25 3.44036 8.69036 4 8 4Z"
                    fill="#1B1B1F"
                  />
                  <path
                    d="M8 9.25C7.30964 9.25 6.75 8.69036 6.75 8C6.75 7.30964 7.30964 6.75 8 6.75C8.69036 6.75 9.25 7.30964 9.25 8C9.25 8.69036 8.69036 9.25 8 9.25Z"
                    fill="#1B1B1F"
                  />
                  <path
                    d="M8 14.5C7.30964 14.5 6.75 13.9404 6.75 13.25C6.75 12.5596 7.30964 12 8 12C8.69036 12 9.25 12.5596 9.25 13.25C9.25 13.9404 8.69036 14.5 8 14.5Z"
                    fill="#1B1B1F"
                  />
                </svg>
              </a>
            </div>
          )}
          image={
            layout === 'grid'
              ? () => (
                  <AdImage
                    aspectClass="aspect-3/4"
                    height={bapAd.image.height}
                    width={bapAd.image.width}
                    url={bapAd.image.url}
                    crop={true}
                    sizes={IMAGE_SIZES}
                  />
                )
              : undefined
          }
        />
      </div>
    </>
  );
}
